// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect } from "react";
import Select from 'react-select';
import "./TimezoneDropdown.scss";

// Get all available timezone identifiers using Intl API
function getTimezoneOptions() {
    const timezones = Intl.supportedValuesOf('timeZone');
    
    // Create array with timezone data including offset in minutes for sorting
    const timezoneData = timezones.map(zone => {
        const offsetMinutes = getTimezoneOffsetMinutes(zone);
        
        return {
            value: zone,
            label: formatTimezoneLabel(zone, offsetMinutes),
            offsetMinutes: offsetMinutes
        };
    });
    
    // Sort by offset
    return timezoneData
        .sort((a, b) => a.offsetMinutes - b.offsetMinutes)
        .map(tz => ({ value: tz.value, label: tz.label }));
}

// Format timezone for display
function formatTimezoneLabel(timezone, offsetMinutes) {
    try {
        // Get current offset for this timezone
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'long',
            hour: 'numeric',
            minute: 'numeric',
            hour12: false
        });
        
        // Get the timezone name from the formatted string
        const formatted = formatter.format(now);
        const tzPart = formatted.split(',')[1]?.trim() || timezone;
        
        // Get the UTC offset as string like "GMT+01:00"
        const sign = offsetMinutes >= 0 ? '+' : '-';
        const hours = Math.abs(Math.floor(offsetMinutes / 60));
        const minutes = Math.abs(offsetMinutes % 60);
        const offset = `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        
        // Format as "offset tzPart"
        return `${offset} ${tzPart}`;
    } catch (e) {
        return timezone;
    }
}

// Get timezone offset in minutes
function getTimezoneOffsetMinutes(timezone) {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const offset = (tzDate - utcDate) / 60000; // in minutes
    return offset;
}

export default function TimezoneDropdown({ 
    value, 
    onChange, 
    className = "" 
}) {
    const [timezoneOptions, setTimezoneOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        const options = getTimezoneOptions();
        setTimezoneOptions(options);
        
        // Set initial selected option
        if (value) {
            const selected = options.find(option => option.value === value);
            setSelectedOption(selected);
        }
    }, [value]);

    // Handle react-select onChange which returns the selected option object
    const handleSelectChange = (selectedOption) => {
        setSelectedOption(selectedOption);
        
        // Create a synthetic event to mimic the HTML select onChange
        const syntheticEvent = {
            target: {
                value: selectedOption.value
            }
        };
        
        onChange(syntheticEvent);
    };

    return (
        <Select
            className={`timezone-select ${className}`}
            classNamePrefix="timezone-select"
            value={selectedOption}
            onChange={handleSelectChange}
            options={timezoneOptions}
            isSearchable={true}
            placeholder="Search timezone..."
            noOptionsMessage={() => "No timezone found"}
        />
    );
} 